import type { Recuerdo } from '#components/mobile/budget/recuerdos';
import type { AspectoBotella } from '#components/mobile/budget/recuerdosAspecto';

import { ANCHO_BOTELLA, cotas, LABIO } from './geometria';
import type { Destello } from './geometria';

/**
 * La llegada de la botella como datos: modo de la escena, disposición en el
 * escenario de diseño (412 × 844, el del prototipo aprobado) y la línea de
 * tiempo de la coreografía (pasos WAAPI y vibraciones). Funciones puras; el
 * componente `LlegadaBotella` solo las reproduce.
 */

/** Escenario de diseño (se escala al hueco disponible). */
export const ESCENARIO = { ancho: 412, alto: 844 } as const;
/** Línea del horizonte (donde empieza el mar). */
export const HORIZONTE = 462;
/** Base de la botella al emerger y al final (tras acercar la cámara). */
const BASE_Y = 600;
const FIN_BASE = 648;
/** Acercamiento de la cámara y centro del escenario. */
export const ZOOM = 1.3;
const CENTRO_X = 206;
/** Izquierda de la botella antes de la cámara. */
export const X_BOTELLA = 146;
/** Carta: margen y ancho (como el prototipo). */
export const CARTA = { x: 36, ancho: 340 } as const;
/** Arriba de las acciones finales. */
export const Y_ACCIONES = 762;

/** Lo que se tarda en llegar a la carta legible (≤ 4 s, F1 §2). */
export const DURACION_LLEGADA = 3800;
/** La versión abreviada (otro dispositivo ya la abrió) empieza aquí. */
export const INICIO_ABREVIADA = 1600;
/** Tras «Guardar»: vuelo, página y sello. */
export const DURACION_GUARDAR = 1200;

export type ModoLlegada = 'completa' | 'abreviada' | 'final';

/**
 * Completa la primera vez; abreviada si otro dispositivo ya la abrió;
 * composición final directa con «reducir movimiento».
 */
export function modoLlegada({
  reducido,
  abiertaPorOtro,
}: {
  reducido: boolean;
  abiertaPorOtro: boolean;
}): ModoLlegada {
  if (reducido) {
    return 'final';
  }
  return abiertaPorOtro ? 'abreviada' : 'completa';
}

export type PieCarta =
  /** Este dispositivo la abre hoy. */
  | { tipo: 'abierta'; fecha: string }
  /** Ya la abrió otro dispositivo (la pareja). */
  | { tipo: 'abierta-por'; fecha: string; quien: string | null }
  /** «Solo ver» y nadie la ha abierto aún. */
  | { tipo: 'pendiente' };

/** Qué dice el pie de la carta. */
export function pieCarta(
  r: Pick<Recuerdo, 'abierta' | 'aportes'>,
  disp: string,
  hoy: string,
  soloLectura: boolean,
): PieCarta {
  const mia = r.aportes[disp]?.abiertaEn;
  if (r.abierta && (!mia || r.abierta.fecha < mia)) {
    return {
      tipo: 'abierta-por',
      fecha: r.abierta.fecha,
      quien: r.abierta.quien,
    };
  }
  if (mia) {
    return { tipo: 'abierta', fecha: mia };
  }
  return soloLectura ? { tipo: 'pendiente' } : { tipo: 'abierta', fecha: hoy };
}

/** Otro dispositivo la abrió antes (secuencia abreviada, sin volver a sellar). */
export function abiertaPorOtro(
  r: Pick<Recuerdo, 'abierta' | 'aportes'>,
  disp: string,
): boolean {
  return !!r.abierta && !r.aportes[disp]?.abiertaEn;
}

export type Disposicion = {
  /** Arriba del contenedor de la botella (antes de la cámara). */
  arriba: number;
  /** Alto del lienzo de la botella. */
  lienzo: number;
  /** Traslado de la cámara final. */
  dx: number;
  dy: number;
  /** Altura de la boca de la botella con la cámara final. */
  boca: number;
  /** Arriba de la carta (alto medido `altoCarta`). */
  arribaCarta: number;
  altoCarta: number;
  /** Transform final de la cámara. */
  camara: string;
};

/** Dónde va cada pieza en el escenario según la botella y el alto de la carta. */
export function disposicion(a: AspectoBotella, altoCarta: number): Disposicion {
  const lienzo = cotas(a).lienzo;
  const arriba = BASE_Y - lienzo;
  const dx = CENTRO_X - (ANCHO_BOTELLA / 2) * ZOOM - X_BOTELLA;
  const dy = FIN_BASE - lienzo * ZOOM - arriba;
  const boca = FIN_BASE - lienzo * ZOOM + LABIO * ZOOM;
  const arribaCarta = Math.max(14, boca - altoCarta + 6);
  return {
    arriba,
    lienzo,
    dx: +dx.toFixed(2),
    dy: +dy.toFixed(2),
    boca: +boca.toFixed(2),
    arribaCarta: +arribaCarta.toFixed(2),
    altoCarta,
    camara: `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px) scale(${ZOOM})`,
  };
}

/** Arriba de la cartela (cifra y tiempo) bajo la botella. */
export const Y_CARTELA = FIN_BASE + 10;
/** Centro horizontal de la escena (anillo y chispa). */
export const X_CENTRO = CENTRO_X;

/** Piezas que mueve la coreografía (atributo `data-parte`). */
export type Parte =
  | 'velo'
  | 'cielo'
  | 'mar'
  | 'emerge'
  | 'rizo'
  | 'gota0'
  | 'gota1'
  | 'gota2'
  | 'brillo'
  | 'gavi'
  | 'dice'
  | 'camara'
  | 'velo2'
  | 'frente'
  | 'cartela'
  | 'corcho'
  | 'lacre-izq'
  | 'lacre-der'
  | 'rollo'
  | 'carta'
  | 'anillo'
  | `rombo${number}`
  | 'estrella'
  | 'acciones'
  | 'vuelo'
  | 'pagina'
  | 'sello'
  | 'mancha'
  | 'fin';

export type Paso = {
  parte: Parte;
  fotogramas: Keyframe[];
  retraso: number;
  duracion: number;
  curva?: string;
  /** Segundo tramo: se crea fuera del gesto (arranque en dos tiempos, R2-D §5). */
  tarde?: boolean;
};

export type Vibracion = { t: number; patron: number[] };

export const CURVA = {
  suave: 'cubic-bezier(.2,.8,.2,1)',
  muelle:
    'linear(0,.12 4%,.42 10%,.78 18%,.98 26%,1.045 32%,1.05 36%,1.02 46%,.995 58%,.998 70%,1)',
  salto: 'linear(0,-.03 10%,-.01 16%,.42 26%,.7 38%,.88 54%,.97 72%,1)',
  sello: 'linear(0,.55 30%,.92 55%,1.03 70%,1)',
} as const;

/** Vibraciones de la llegada (R2-D §7; ≤ 60 ms por pulso). */
export const VIBRACION = {
  asienta: [8, 30, 8, 30, 40],
  corcho: [12, 40, 18],
  sello: [8, 30, 8, 30, 40],
  selloReducido: [10],
} as const;

/** Vibraciones según el modo (la abreviada omite las que ya pasaron). */
export function vibracionesLlegada(modo: ModoLlegada): Vibracion[] {
  if (modo === 'final') {
    return [{ t: 0, patron: [...VIBRACION.asienta] }];
  }
  const todas: Vibracion[] = [
    { t: 1350, patron: [...VIBRACION.asienta] },
    { t: 2200, patron: [...VIBRACION.corcho] },
  ];
  if (modo === 'completa') {
    return todas;
  }
  return todas
    .filter(v => v.t >= INICIO_ABREVIADA)
    .map(v => ({ ...v, t: v.t - INICIO_ABREVIADA }));
}

const T0 = 'translate(0px, 0px)';

/** La coreografía de la llegada (F1 §2 «Al cumplir», tabla de R2-D §2). */
export function lineaLlegada(
  d: Disposicion,
  destellos: readonly Destello[],
): Paso[] {
  const k0 = d.boca - d.arribaCarta;
  const h = d.altoCarta;
  const pasos: Paso[] = [
    // 0-0,5 s: velo, cielo y mar que sube.
    {
      parte: 'velo',
      fotogramas: [{ opacity: 0 }, { opacity: 1 }],
      retraso: 0,
      duracion: 400,
    },
    {
      parte: 'cielo',
      fotogramas: [{ opacity: 0 }, { opacity: 1 }],
      retraso: 0,
      duracion: 500,
      curva: 'ease-out',
    },
    {
      parte: 'mar',
      fotogramas: [
        { transform: 'translateY(420px)' },
        { transform: 'translateY(0px)' },
      ],
      retraso: 0,
      duracion: 560,
      curva: CURVA.suave,
    },
    // 0,5-1,6 s: la botella emerge, rizo, gotas, brillo y Gavi.
    {
      parte: 'emerge',
      fotogramas: [
        { transform: 'translateY(300px) rotate(-14deg)' },
        { transform: 'translateY(0px) rotate(0deg)' },
      ],
      retraso: 500,
      duracion: 1000,
      curva: CURVA.muelle,
    },
    {
      parte: 'rizo',
      fotogramas: [
        { transform: 'scale(.4)', opacity: 0 },
        { opacity: 0.7, offset: 0.08 },
        { transform: 'scale(1.6)', opacity: 0 },
      ],
      retraso: 1150,
      duracion: 800,
      curva: 'ease-out',
    },
    ...([0, 1, 2] as const).map(
      (i): Paso => ({
        parte: `gota${i}`,
        fotogramas: [
          { transform: 'translateY(0px)', opacity: 0 },
          { opacity: 1, offset: 0.2 },
          { transform: 'translateY(46px)', opacity: 0 },
        ],
        retraso: 1050 + i * 150,
        duracion: 700,
        curva: 'ease-in',
      }),
    ),
    {
      parte: 'brillo',
      fotogramas: [
        { transform: 'translateX(0px) skewX(-14deg)', opacity: 0 },
        { opacity: 1, offset: 0.15 },
        { opacity: 1, offset: 0.85 },
        { transform: 'translateX(260px) skewX(-14deg)', opacity: 0 },
      ],
      retraso: 1050,
      duracion: 650,
      curva: 'cubic-bezier(.45,0,.3,1)',
    },
    {
      parte: 'gavi',
      fotogramas: [
        { transform: 'translate(-70px, -90px) rotate(-12deg)', opacity: 0 },
        { opacity: 1, offset: 0.3 },
        { transform: `${T0} rotate(0deg)`, opacity: 1 },
      ],
      retraso: 800,
      duracion: 600,
      curva: CURVA.suave,
    },
    {
      parte: 'dice',
      fotogramas: [
        { opacity: 0, transform: 'translateY(4px)' },
        { opacity: 1, transform: 'translateY(0px)', offset: 0.3 },
        { opacity: 1, transform: 'translateY(0px)', offset: 0.75 },
        { opacity: 0, transform: 'translateY(0px)' },
      ],
      retraso: 1300,
      duracion: 1000,
      curva: 'ease-out',
    },
    // 1,6-2,2 s: la cámara acerca la botella; cartela.
    {
      parte: 'camara',
      fotogramas: [{ transform: `${T0} scale(1)` }, { transform: d.camara }],
      retraso: 1600,
      duracion: 600,
      curva: 'cubic-bezier(.3,.7,.2,1)',
    },
    {
      parte: 'velo2',
      fotogramas: [{ opacity: 0 }, { opacity: 1 }],
      retraso: 1600,
      duracion: 600,
    },
    {
      parte: 'frente',
      fotogramas: [{ opacity: 1 }, { opacity: 0 }],
      retraso: 1600,
      duracion: 400,
    },
    {
      parte: 'cartela',
      fotogramas: [
        { opacity: 0, transform: 'translateY(8px)' },
        { opacity: 1, transform: 'translateY(0px)' },
      ],
      retraso: 1800,
      duracion: 400,
      curva: 'ease-out',
    },
    // 2,2-2,8 s: corcho y lacre.
    {
      parte: 'corcho',
      fotogramas: [
        { transform: 'translateY(0px) rotate(0deg)', opacity: 1 },
        { opacity: 1, offset: 0.8 },
        { transform: 'translateY(-560px) rotate(160deg)', opacity: 0 },
      ],
      retraso: 2200,
      duracion: 620,
      curva: CURVA.salto,
      tarde: true,
    },
    {
      parte: 'lacre-izq',
      fotogramas: [
        { transform: `${T0} rotate(0deg)`, opacity: 1 },
        { opacity: 1, offset: 0.6 },
        { transform: 'translate(-26px, 120px) rotate(-70deg)', opacity: 0 },
      ],
      retraso: 2200,
      duracion: 560,
      curva: 'cubic-bezier(.5,0,.9,.5)',
      tarde: true,
    },
    {
      parte: 'lacre-der',
      fotogramas: [
        { transform: `${T0} rotate(0deg)`, opacity: 1 },
        { opacity: 1, offset: 0.6 },
        { transform: 'translate(30px, 110px) rotate(80deg)', opacity: 0 },
      ],
      retraso: 2240,
      duracion: 560,
      curva: 'cubic-bezier(.5,0,.9,.5)',
      tarde: true,
    },
    // 2,45-3,55 s: la carta sale del cuello (solo transform: se desliza
    // dentro de un marco que la recorta), anillo y chispa.
    {
      parte: 'rollo',
      fotogramas: [
        { transform: `translateY(${k0}px) scaleX(.1)`, opacity: 0 },
        { opacity: 1, offset: 0.1 },
        {
          transform: `translateY(${h}px) scaleX(1)`,
          opacity: 1,
          offset: 0.3,
          easing: 'cubic-bezier(.3,.6,.3,1)',
        },
        { transform: 'translateY(0px) scaleX(1)', opacity: 1, offset: 0.86 },
        { transform: 'translateY(-4px) scaleX(1)', opacity: 0 },
      ],
      retraso: 2450,
      duracion: 1100,
      tarde: true,
    },
    {
      parte: 'carta',
      fotogramas: [
        { transform: `translateY(${h}px)` },
        {
          transform: `translateY(${h}px)`,
          offset: 0.3,
          easing: 'cubic-bezier(.3,.6,.3,1)',
        },
        { transform: 'translateY(0px)', offset: 0.86 },
        { transform: 'translateY(0px)' },
      ],
      retraso: 2450,
      duracion: 1100,
      tarde: true,
    },
    {
      parte: 'anillo',
      fotogramas: [
        { transform: 'scale(.6)', opacity: 0 },
        { opacity: 0.6, offset: 0.08 },
        { transform: 'scale(1.6)', opacity: 0 },
      ],
      retraso: 2500,
      duracion: 800,
      curva: 'ease-out',
      tarde: true,
    },
    ...destellos.map(
      ([x, y, s], i): Paso => ({
        parte: `rombo${i}`,
        fotogramas: [
          { transform: `${T0} rotate(45deg) scale(.2)`, opacity: 0 },
          { opacity: 1, offset: 0.25 },
          {
            transform: `translate(${x}px, ${y}px) rotate(45deg) scale(${s})`,
            opacity: 0,
          },
        ],
        retraso: 2550 + i * 25,
        duracion: 900,
        curva: 'cubic-bezier(.1,.7,.3,1)',
        tarde: true,
      }),
    ),
    {
      parte: 'estrella',
      fotogramas: [
        { transform: 'scale(0) rotate(0deg)', opacity: 0 },
        { transform: 'scale(1) rotate(45deg)', opacity: 1, offset: 0.4 },
        { transform: 'scale(.2) rotate(90deg)', opacity: 0 },
      ],
      retraso: 2700,
      duracion: 800,
      curva: 'ease-out',
      tarde: true,
    },
    // 3,4 s: final legible; las acciones aparecen y no avanza sola.
    {
      parte: 'acciones',
      fotogramas: [
        { opacity: 0, transform: 'translateY(6px)' },
        { opacity: 1, transform: 'translateY(0px)' },
      ],
      retraso: 3400,
      duracion: 400,
      curva: 'ease-out',
      tarde: true,
    },
  ];
  return pasos;
}

/** Tras «Guardar en la bitácora»: la carta se pliega, la botella vuela, cae el sello. */
export function lineaGuardar(conSello: boolean): Paso[] {
  const pasos: Paso[] = [
    {
      parte: 'acciones',
      fotogramas: [{ opacity: 1 }, { opacity: 0 }],
      retraso: 0,
      duracion: 180,
    },
    {
      parte: 'cartela',
      fotogramas: [{ opacity: 1 }, { opacity: 0 }],
      retraso: 0,
      duracion: 200,
    },
    {
      parte: 'carta',
      fotogramas: [
        { transform: 'translateY(0px) scaleY(1)', opacity: 1 },
        { transform: 'translateY(-30px) scaleY(.05)', opacity: 0 },
      ],
      retraso: 0,
      duracion: 360,
      curva: 'cubic-bezier(.5,0,.8,.4)',
    },
    {
      parte: 'vuelo',
      fotogramas: [
        { transform: `${T0} scale(1)`, opacity: 1 },
        { opacity: 1, offset: 0.7 },
        { transform: 'translate(150px, -620px) scale(.25)', opacity: 0 },
      ],
      retraso: 80,
      duracion: 640,
      curva: 'cubic-bezier(.6,0,.25,1)',
    },
    {
      parte: 'pagina',
      fotogramas: [
        { transform: 'translateY(900px)', opacity: 0 },
        { opacity: 1, offset: 0.15 },
        { transform: 'translateY(0%)', opacity: 1 },
      ],
      retraso: 150,
      duracion: 520,
      curva: CURVA.suave,
    },
  ];
  if (conSello) {
    pasos.push(
      {
        parte: 'sello',
        fotogramas: [
          { transform: 'scale(1.3) rotate(-6deg)', opacity: 0 },
          { opacity: 0.95, offset: 0.25 },
          { transform: 'scale(1) rotate(0deg)', opacity: 0.95 },
        ],
        retraso: 760,
        duracion: 300,
        curva: CURVA.sello,
      },
      {
        parte: 'mancha',
        fotogramas: [
          { opacity: 0, transform: 'scale(.8)' },
          { opacity: 0.9, transform: 'scale(1)' },
        ],
        retraso: 800,
        duracion: 300,
        curva: 'ease-out',
      },
    );
  }
  pasos.push({
    parte: 'fin',
    fotogramas: [
      { opacity: 0, transform: 'translateY(6px)' },
      { opacity: 1, transform: 'translateY(0px)' },
    ],
    retraso: conSello ? 1050 : 700,
    duracion: 250,
    curva: 'ease-out',
  });
  return pasos;
}

/** Vibración al sellar (una sola, a la vez que cae el sello). */
export function vibracionesGuardar(
  reducido: boolean,
  conSello: boolean,
): Vibracion[] {
  if (!conSello) {
    return [];
  }
  return reducido
    ? [{ t: 0, patron: [...VIBRACION.selloReducido] }]
    : [{ t: 840, patron: [...VIBRACION.sello] }];
}

/** Fin de la línea de tiempo (lo que tarda la última pieza). */
export function finDe(pasos: readonly Paso[]): number {
  return pasos.reduce((m, p) => Math.max(m, p.retraso + p.duracion), 0);
}

/** Escala del escenario de diseño para que quepa entero en la pantalla. */
export function escalaEscenario(ancho: number, alto: number): number {
  if (!(ancho > 0) || !(alto > 0)) {
    return 1;
  }
  return +Math.min(ancho / ESCENARIO.ancho, alto / ESCENARIO.alto).toFixed(4);
}

// ---------------------------------------------------------------------------
// Marca local de «ya la vi» (solo para «Solo ver», que no escribe nada)
// ---------------------------------------------------------------------------

const PREFIJO_VISTA = 'rumbo-llegada-vista:';

/** La llegada ya se vio en este dispositivo en modo «Solo ver». */
export function llegadaVistaLocal(rid: string): boolean {
  try {
    return window.localStorage.getItem(PREFIJO_VISTA + rid) === '1';
  } catch {
    return false;
  }
}

export function marcarVistaLocal(rid: string): void {
  try {
    window.localStorage.setItem(PREFIJO_VISTA + rid, '1');
  } catch {
    // Sin almacenamiento: se volverá a ver, sin más.
  }
}
